import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text,
  Input,
  List,
  ListItem,
  ListIcon,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { getClientDeleteImpact, confirmDeleteClient } from "../../../api/clientsApi";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

/**
 * Confirmation modal for permanently deleting a company (Client) and every
 * dependent record. Fetches the delete impact, lists what will be removed,
 * and requires the user to type the exact confirmation phrase (localized:
 * EN "delete all permanently" / FR "tout supprimer définitivement") before
 * the destructive action is enabled. Used both for a direct delete and for
 * confirming an Excel-initiated pending delete.
 */
const DeleteCompanyConfirmModal = ({
  isOpen,
  onClose,
  clientId,
  clientName,
  buId,
  onSuccess,
}) => {
  const { t } = useTranslation("portal");
  const toast = useToast();

  // Locale-specific phrase the user must type. The backend accepts either
  // language case-insensitively, so this only drives the UI.
  const expectedPhrase = t("pilotClients.list.deleteConfirm.phrase");

  const [impact, setImpact] = useState(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [impactError, setImpactError] = useState(false);
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !clientId) return undefined;
    let active = true;
    setImpact(null);
    setImpactError(false);
    setTyped("");
    setLoadingImpact(true);
    getClientDeleteImpact(clientId, { buId: buId || undefined })
      .then((data) => {
        if (active) setImpact(data?.impact || null);
      })
      .catch(() => {
        if (active) setImpactError(true);
      })
      .finally(() => {
        if (active) setLoadingImpact(false);
      });
    return () => {
      active = false;
    };
  }, [isOpen, clientId, buId]);

  const canConfirm =
    typed.trim().toLowerCase() === expectedPhrase.toLowerCase() && !submitting;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await confirmDeleteClient(clientId, typed.trim(), {
        buId: buId || undefined,
      });
      toast({
        title: t("pilotClients.list.toasts.confirmDeleteSuccess"),
        status: "success",
      });
      onSuccess?.();
      onClose?.();
    } catch (e) {
      toast({
        title: t("pilotClients.list.toasts.confirmDeleteFailedTitle"),
        description:
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          t("pilotClients.list.toasts.confirmDeleteFailedDescription"),
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const impactRows = [];
  if (impact) {
    if (impact.needs) impactRows.push(t("pilotClients.list.deleteConfirm.needs", { count: impact.needs }));
    if (impact.matches) impactRows.push(t("pilotClients.list.deleteConfirm.matches", { count: impact.matches }));
    if (impact.contacts) impactRows.push(t("pilotClients.list.deleteConfirm.contacts", { count: impact.contacts }));
    if (impact.comments) impactRows.push(t("pilotClients.list.deleteConfirm.comments", { count: impact.comments }));
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          {t("pilotClients.list.deleteConfirm.title")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={4}>
            {t("pilotClients.list.deleteConfirm.intro", {
              name: clientName || "",
            })}
          </Text>

          {loadingImpact && (
            <Box display="flex" alignItems="center" gap={2} mb={4}>
              <Spinner size="sm" />
              <Text sx={{ marginBottom: "0 !important" }}>
                {t("pilotClients.list.deleteConfirm.loadingImpact")}
              </Text>
            </Box>
          )}

          {impactError && (
            <Text color="portal.danger" mb={4}>
              {t("pilotClients.list.deleteConfirm.impactError")}
            </Text>
          )}

          {!loadingImpact && !impactError && (
            <Box mb={4}>
              <Text fontWeight="600" mb={2}>
                {t("pilotClients.list.deleteConfirm.summaryHeading")}
              </Text>
              <List spacing={1}>
                <ListItem>
                  <ListIcon as={WarningTwoIcon} color="portal.danger" />
                  {t("pilotClients.list.deleteConfirm.company")}
                </ListItem>
                {impactRows.map((label) => (
                  <ListItem key={label}>
                    <ListIcon as={WarningTwoIcon} color="portal.danger" />
                    {label}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Text mb={2}>
            {t("pilotClients.list.deleteConfirm.prompt", { phrase: expectedPhrase })}
          </Text>
          <Input
            data-testid="delete-confirm-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={expectedPhrase}
            autoFocus
          />
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={submitting}>
            {t("pilotClients.list.deleteConfirm.cancel")}
          </Button>
          <Button
            colorScheme="red"
            data-testid="delete-confirm-submit"
            onClick={handleConfirm}
            isDisabled={!canConfirm}
            isLoading={submitting}
          >
            {t("pilotClients.list.deleteConfirm.confirm")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteCompanyConfirmModal;
